import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { PRODUCT_FEATURES, INDUSTRIES_NAV, RESOURCES } from "./navData";

const DropdownItem = ({ item, onClick }) => (
  <Link to={item.href} className="mk-dropdown-item" onClick={onClick} data-testid={`nav-dd-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <span className="w-9 h-9 rounded-lg bg-[var(--mk-blue-light)] flex items-center justify-center shrink-0">
      <item.icon className="w-4 h-4 text-[var(--mk-blue)]" />
    </span>
    <span className="min-w-0">
      <span className="block text-[14px] font-semibold text-slate-900 leading-tight">{item.label}</span>
      <span className="block text-[12px] text-slate-500 mt-0.5">{item.desc}</span>
    </span>
  </Link>
);

const Dropdown = ({ label, items, cols = 2, open, onToggle, onClose, testId, footerLink }) => {
  const closeTimer = useRef(null);
  const openNow = () => {
    clearTimeout(closeTimer.current);
    onToggle(true);
  };
  const closeSoon = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => onToggle(false), 160);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button
        className={`mk-nav-link ${open ? "text-slate-900" : ""}`}
        onClick={() => onToggle(!open)}
        data-testid={testId}
        aria-expanded={open}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className={`mk-dropdown-panel ${cols === 2 ? "w-[540px]" : "w-[300px]"}`}
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
        >
          <div className={`grid gap-0.5 ${cols === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
            {items.map((item) => (
              <DropdownItem key={item.label} item={item} onClick={onClose} />
            ))}
          </div>
          {footerLink && (
            <Link
              to={footerLink.href}
              onClick={onClose}
              className="flex items-center justify-center gap-1.5 mt-2 pt-3 border-t border-slate-100 text-[13.5px] font-semibold text-[var(--mk-blue)] hover:text-[var(--mk-blue-hover)]"
              data-testid={footerLink.testId}
            >
              {footerLink.label} <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

const MobileGroup = ({ label, items, onNavigate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100">
      <button
        className="w-full flex items-center justify-between py-3.5 text-[15px] font-semibold text-slate-900"
        onClick={() => setOpen((v) => !v)}
        data-testid={`mobile-group-${label.toLowerCase()}`}
      >
        {label}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-3 space-y-0.5">
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              onClick={onNavigate}
              className="flex items-center gap-3 py-2.5 px-2 rounded-lg text-[14px] text-slate-600 hover:bg-slate-50"
            >
              <item.icon className="w-4 h-4 text-[var(--mk-blue)]" />
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const MarketingNav = () => {
  const [openDd, setOpenDd] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDd(null);
  }, [location.pathname, location.hash]);

  const closeAll = () => setOpenDd(null);
  const toggleDd = (name) => (v) => setOpenDd((prev) => (v ? name : prev === name ? null : prev));

  return (
    <nav
      className={`mk fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-xl border-b transition-shadow ${
        scrolled ? "border-slate-200/80 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.1)]" : "border-slate-200/50"
      }`}
      data-testid="marketing-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-[72px]">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo-link">
          <img src="/logo-dark.png" alt="Fieldz AI — Field Service Management Software" className="h-9 md:h-10 object-contain" />
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          <Dropdown
            label="Product"
            items={PRODUCT_FEATURES}
            cols={2}
            open={openDd === "product"}
            onToggle={toggleDd("product")}
            onClose={closeAll}
            testId="nav-product-dropdown"
            footerLink={{ href: "/features", label: "View all 15 features", testId: "nav-all-features-link" }}
          />
          <Dropdown
            label="Industries"
            items={INDUSTRIES_NAV}
            cols={2}
            open={openDd === "industries"}
            onToggle={toggleDd("industries")}
            onClose={closeAll}
            testId="nav-industries-dropdown"
            footerLink={{ href: "/industries", label: "View all industries", testId: "nav-all-industries-link" }}
          />
          <Link to="/multi-location" className="mk-nav-link" data-testid="nav-multi-location-link">
            Multi-Location
          </Link>
          <Link to="/pricing" className="mk-nav-link" data-testid="nav-pricing-link">
            Pricing
          </Link>
          <Dropdown
            label="Resources"
            items={RESOURCES}
            cols={2}
            open={openDd === "resources"}
            onToggle={toggleDd("resources")}
            onClose={closeAll}
            testId="nav-resources-dropdown"
          />
        </div>

        <div className="hidden lg:flex items-center gap-2.5">
          <Link to="/login" className="mk-btn mk-btn-ghost mk-btn-sm" data-testid="nav-login-btn">
            Login
          </Link>
          <Link to="/register" className="mk-btn mk-btn-primary mk-btn-sm" data-testid="nav-signup-btn">
            Start Free Trial
          </Link>
        </div>

        <button
          className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-btn"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-5 pb-6 max-h-[calc(100vh-64px)] overflow-y-auto" data-testid="mobile-menu-panel">
          <MobileGroup label="Product" items={PRODUCT_FEATURES} onNavigate={() => setMobileOpen(false)} />
          <MobileGroup label="Industries" items={INDUSTRIES_NAV} onNavigate={() => setMobileOpen(false)} />
          <MobileGroup label="Resources" items={RESOURCES} onNavigate={() => setMobileOpen(false)} />
          <Link to="/multi-location" onClick={() => setMobileOpen(false)} className="block py-3.5 text-[15px] font-semibold text-slate-900 border-b border-slate-100">
            Multi-Location
          </Link>
          <Link to="/pricing" onClick={() => setMobileOpen(false)} className="block py-3.5 text-[15px] font-semibold text-slate-900 border-b border-slate-100" data-testid="mobile-pricing-link">
            Pricing
          </Link>
          <div className="pt-4 space-y-2.5">
            <Link to="/login" onClick={() => setMobileOpen(false)} className="mk-btn mk-btn-outline w-full" data-testid="mobile-login-btn">
              Login
            </Link>
            <Link to="/register" onClick={() => setMobileOpen(false)} className="mk-btn mk-btn-primary w-full" data-testid="mobile-signup-btn">
              Start Free Trial
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default MarketingNav;
